import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, FileDashed } from "@phosphor-icons/react";

export default function InhouseForm({ user, staff, onDone, draft = null }) {
  const [company, setCompany] = useState("");
  const [otherCompany, setOtherCompany] = useState("");
  const [driverId, setDriverId] = useState(draft?.driver_id || (user.role === "driver" ? user.id : ""));
  const [plate, setPlate] = useState(draft?.vehicle_plate || "");
  const [notes, setNotes] = useState(draft?.notes || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [newCompany, setNewCompany] = useState("");

  const loadCompanies = () => api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  useEffect(() => { loadCompanies(); }, []);

  useEffect(() => {
    if (draft?.company && companies.length) {
      if (companies.some((c) => c.name === draft.company)) setCompany(draft.company);
      else { setCompany("Other"); setOtherCompany(draft.company); }
    }
  }, [draft, companies]);

  const canAddCompany = ["admin", "ceo", "coceo", "dev"].includes(user.role);

  const addCompany = async () => {
    if (!newCompany.trim()) return;
    try {
      await api.post("/companies", { name: newCompany.trim() });
      toast.success("Company added");
      setNewCompany("");
      loadCompanies();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const finalCompany = () => (company === "Other" ? otherCompany.trim() : company);

  const submit = async (e) => {
    e.preventDefault();
    if (!company) {
      toast.error("Please select a company");
      return;
    }
    if (!finalCompany()) {
      toast.error("Please enter the company name");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/submissions", {
        type: "inhouse",
        company: finalCompany(),
        driver_id: driverId || user.id,
        vehicle_plate: plate,
        notes,
      });
      if (draft?.id) await api.delete(`/submissions/${draft.id}`).catch(() => {});
      setDone(data);
      toast.success(`Job ${data.job_no} submitted`);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.post("/submissions", {
        type: "inhouse", draft: true,
        company: finalCompany(),
        driver_id: driverId || user.id,
        vehicle_plate: plate,
        notes,
      });
      if (draft?.id) await api.delete(`/submissions/${draft.id}`).catch(() => {});
      toast.success("Draft saved — find it under the Drafts tab");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-sm border border-border bg-card p-12 text-center animate-fade-up" data-testid="inhouse-success">
        <CheckCircle size={56} weight="fill" className="text-emerald-400" />
        <h3 className="font-display text-2xl font-bold">Referral Submitted</h3>
        <p className="font-mono text-primary">{done.job_no}</p>
        <p className="text-sm text-muted-foreground">No invoice required for inhouse referrals.</p>
        <Button data-testid="inhouse-new-button" onClick={() => { setDone(null); setCompany(""); setOtherCompany(""); setPlate(""); setNotes(""); }}
          variant="outline" className="rounded-sm border-input hover:bg-secondary hover:text-foreground">
          Submit Another
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6 rounded-sm border border-border bg-card p-6 sm:p-8" data-testid="inhouse-form">
      {draft && (
        <div className="flex items-center gap-3 rounded-sm border border-amber-500/40 bg-amber-500/10 px-4 py-3" data-testid="draft-resume-banner">
          <FileDashed size={18} className="shrink-0 text-amber-400" />
          <p className="text-xs font-semibold text-amber-300">Resuming draft <span className="font-mono">{draft.job_no}</span> — saved data pre-filled.</p>
        </div>
      )}
      <div>
        <h3 className="font-display text-xl font-bold tracking-tight">Inhouse Towing Referral</h3>
        <p className="mt-1 text-sm text-muted-foreground">Simplified referral flow — no invoice generated.</p>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Company *</Label>
        <Select value={company} onValueChange={setCompany}>
          <SelectTrigger data-testid="inhouse-company-select" className="rounded-sm border-input bg-background focus:ring-primary">
            <SelectValue placeholder="Select referral company" />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            {companies.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {company === "Other" && (
          <Input data-testid="inhouse-other-company-input" value={otherCompany} onChange={(e) => setOtherCompany(e.target.value)}
            placeholder="Type the company / workshop name..."
            className="rounded-sm border-input bg-background focus-visible:ring-primary" />
        )}
        {canAddCompany && (
          <div className="flex gap-2 pt-2">
            <Input data-testid="add-company-input" value={newCompany} onChange={(e) => setNewCompany(e.target.value)}
              placeholder="Add new company..." className="rounded-sm border-input bg-background focus-visible:ring-primary" />
            <Button type="button" variant="outline" data-testid="add-company-button" onClick={addCompany}
              className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">Add</Button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Staff Name [Driver] *</Label>
        <Select value={driverId} onValueChange={setDriverId}>
          <SelectTrigger data-testid="inhouse-driver-select" className="rounded-sm border-input bg-background focus:ring-primary">
            <SelectValue placeholder="Select driver" />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            {(staff?.drivers || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Vehicle Plate</Label>
        <Input data-testid="inhouse-plate-input" value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())}
          placeholder="e.g. ABC 1234" className="rounded-sm border-input bg-background font-mono uppercase focus-visible:ring-primary" />
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Notes</Label>
        <Textarea data-testid="inhouse-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional remarks..." className="rounded-sm border-input bg-background focus-visible:ring-primary" />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <Button data-testid="inhouse-submit-button" type="submit" disabled={busy}
          className="flex-1 rounded-sm bg-primary py-6 text-sm font-bold uppercase tracking-wider text-primary-foreground transition-colors hover:bg-amber-400 hover:text-black">
          {busy ? "Submitting..." : "Submit Referral"}
        </Button>
        <Button data-testid="inhouse-draft-button" type="button" variant="outline" disabled={busy} onClick={saveDraft}
          className="rounded-sm border-input px-8 py-6 font-bold uppercase tracking-wider text-muted-foreground hover:bg-secondary hover:text-foreground">
          <FileDashed size={16} className="mr-2" /> Save as Draft
        </Button>
      </div>
    </form>
  );
}
